import { Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CREATE_PLAYER,
  Fixture,
  GET_PLAYER,
  GET_PLAYER_FIXTURES,
  GET_PLAYERS,
  PlayerData,
  UPDATE_PLAYER,
} from '@pool-league/shared-data-api';
import { SetOptional } from '@pool-league/shared-util-misc';
import { PoolLeagueApiClient } from '../base-client/pool-league-api-client';

@Injectable({
  providedIn: 'root',
})
export class PlayerApiClient extends PoolLeagueApiClient {
  readonly #getPlayers = this.buildRequest(GET_PLAYERS);
  readonly #createPlayer = this.buildRequest(CREATE_PLAYER);

  getFixtures(playerId: string) {
    return this.buildRequest(GET_PLAYER_FIXTURES, { playerId }).get<
      Fixture[]
    >();
  }

  /**
   * Get a player by UUID.  The returned observable will emit undefined if the
   * backend returns a 404 (non-existent player ID).  For all other errors, it
   * will emit an HttpErrorResponse to the error callback.
   *
   * @param playerId
   */
  getByIdIfExists(playerId: string): Observable<PlayerData | undefined> {
    return this.buildRequest(GET_PLAYER, { playerId })
      .get<PlayerData>({
        observe: 'body',
      })
      .pipe(
        catchError((e) =>
          e instanceof HttpErrorResponse && e.status === 404
            ? of(undefined)
            : throwError(() => e),
        ),
      );
  }

  getFullProfile(playerId: string): Observable<PlayerData> {
    // TODO: query param or different path to return player + fixtures + results?
    return this.buildRequest(GET_PLAYER, { playerId }).get<PlayerData>({
      observe: 'body',
    });
  }

  getByBbUserName(bbUserName: string): Observable<PlayerData | undefined> {
    return this.#getPlayers
      .get<PlayerData>({
        params: { bbUserName },
        observe: 'body',
      })
      .pipe(
        catchError((e) =>
          e instanceof HttpErrorResponse && e.status === 404
            ? of(undefined)
            : throwError(() => e),
        ),
      );
  }

  /**
   * Create/update a profile (depending on whether an ID is set).
   */
  saveProfile(profile: SetOptional<PlayerData, 'id'>): Observable<PlayerData> {
    if (profile.id) {
      return this.buildRequest(UPDATE_PLAYER, {
        playerId: profile.id,
      }).put<PlayerData>(profile, {
        observe: 'body',
      });
    } else {
      return this.#createPlayer.post<PlayerData>(profile, {
        observe: 'body',
      });
    }
  }

  getAllPlayers(): Observable<PlayerData[]> {
    return this.#getPlayers
      .get<PlayerData[]>({
        observe: 'body',
      })
      .pipe(
        catchError((e) =>
          e instanceof HttpErrorResponse && e.status === 404
            ? of([])
            : throwError(() => e),
        ),
      );
  }
}
